import React, { createRef } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ThemeIcons from '../../../../theme/Icons';

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  margin: ${(props) => props.margin};
  font-size: ${(props) => props.fontSize}px;
  border: ${({ borderWidth, borderColor }) => `${borderWidth}px solid ${borderColor}`};
  border-radius: ${(props) => props.borderRadius}px;
  background-color: ${(props) => props.backgroundColor};
`;

const StyledInput = styled.input`
  width: 100%;
  height: 100%;
  border: 0px;
  background-color: ${(props) => props.backgroundColor};
  font-size: ${(props) => props.fontSize}px;
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  padding: ${(props) => props.padding}px;
  resize: none;

  border: 1px solid transparent;

  &:hover, &:focus, &:focus-visible {
    outline: unset;
    border: 1px solid transparent;
  }

`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  font-size: ${(props) => props.fontSize}px;
  background-color: ${(props) => props.backgroundColor};

  ${({ disabled, primaryColor }) => !disabled &&
    `
    cursor: pointer;

    &:hover {
      color: ${primaryColor || 'blue'};
    }
  `}
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class TableTextField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,

    value: PropTypes.array,
    cellValue: PropTypes.string,
    rowId: PropTypes.string,

    disabled: PropTypes.bool,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onCapture: PropTypes.func,

  }

  static defaultProps = {
    value: ''
  }

  constructor(props) {
    super(props);
    const { cellValue } = props;
    this.state = { focused: false, changes: cellValue };
    this.textareaRef = createRef();
  }

  componentDidUpdate(prevProps) {
    const { changes } = this.state;
    const { cellValue } = this.props;
    if (cellValue !== prevProps.cellValue && cellValue !== changes) this.setState({ changes: cellValue });
  }

  componentWillUnmount() {

    const { cellValue, disabled, fieldId, fieldIdentifier, fieldPositionId, rowId, columnIdentifier, column, onBlur } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== cellValue && !disabled) {

      this.handleCellChange(changes);

      onBlur(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, column, columnIdentifier);
    }

  }

  render() {

    const { focused, changes } = this.state;

    const {
      fieldId,
      fieldIdentifier,
      fieldPositionId,

      rowId,
      column,
      columnIdentifier,

      cellValue,
      disabled,

      //Field style
      primaryColor,
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,

      //Action handlers
      onFocus,
      onBlur,

    } = this.props;

    const displayInput = (
      <StyledInput
        value={changes}
        ref={(e) => {
          this.textareaRef.current = e;
        }}
        {...{
          primaryColor,
          disabled,
          fontSize,
          fontColor,
          fontStyle,
          fontWeight,
          textAlign,
          textTransform,
          textDecoration,
          textOverflow,
          borderColor,
          borderRadius,
          borderWidth,
          backgroundColor,
          padding,
        }}
        onChange={(e) => {
          this.setState({ changes: e.target.value });
        }}
        onFocus={(e) => {

          if (!focused) this.setState({ focused: true });

          if (!disabled && onFocus) {
            onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } }, rowId, column, columnIdentifier);
          }

        }}
        onBlur={(e) => {

          const nextValue = e.target.value;

          if (focused) this.setState({ focused: false });

          if (!disabled && nextValue !== cellValue) this.handleCellChange(nextValue);

          if (onBlur) {

            onBlur(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, column, columnIdentifier);

          }
        }}
      />
    );

    return (
      <StyledWrapper {...{ margin, borderWidth, borderColor, borderRadius, backgroundColor, padding, fontSize }}>
        {displayInput}
        {disabled ? null : (
          <IconWrapper onClick={this.handleBarcodeClick} {...{ primaryColor, disabled, backgroundColor, fontSize }}>
            <FontAwesomeIcon icon={ThemeIcons.fields.barcode} size="lg" />
          </IconWrapper>
        )}
      </StyledWrapper>
    );
  }

  handleCellChange = (nextCellValue) => {

    const { onChange, rowId, column, value, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    const nextValue = [...value];

    const targetRowIndex = value.findIndex((row) => row._id === rowId);
    const nextRow = { ...nextValue[targetRowIndex] };

    /**<----------NOTICE---------------->
     * onChange will only be triggered if the row and column associated
     * with the table cell is undeleted
    */
    if (nextRow && nextRow._id) {

      nextRow.cells = { ...nextRow.cells, [column]: nextCellValue };

      nextValue[targetRowIndex] = nextRow;

      if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);

    }
    else {
      this.setState({ changes: '' });
    }

  }

  handleBarcodeClick = async () => {

    const { onCapture, disabled } = this.props;
    
    if (disabled) return;

    if (this.textareaRef.current) {
      this.textareaRef.current.focus();
    }
    
    if (onCapture) {
      const response = await onCapture();
      this.handleCellChange(response);
    }
  };

}

export default TableTextField;
